<template>
  <tr class="item-row" :class="{ 'bg-light bg-opacity-50': index % 2 === 0 }">
    <!-- # -->
    <td class="text-center align-top" style="width: 30px; color: #8b95a5;">
      {{ index + 1 }}
    </td>
    <!-- Name + Lot + Tax Badge -->
    <td class="align-top" style="min-width: 140px;">
      <span class="fw-medium">{{ item.name }}</span>
      <span
        v-if="taxPolicy !== 'no_tax'"
        class="badge ms-1"
        :class="item.tax_status === 'vatable' ? 'bg-info bg-opacity-10 text-info' : 'bg-secondary bg-opacity-10 text-secondary'"
        style="font-size: 9px; font-weight: 500; vertical-align: middle;"
      >
        {{ item.tax_status === 'vatable' ? $t('VATable') : $t('Non-VAT') }}
      </span>
      <div class="text-muted" style="font-size: 12px;">
        {{ $parent.type === 'EDIT' ? item.lot : lotGenarate }}
      </div>
    </td>
    <!-- Quantity -->
    <td class="align-top">
      <input
        :id="'quantity'+index"
        type="number"
        inputmode="decimal"
        v-model.number="v$.item.ordered_quantity.$model"
        class="form-control shadow-none form-control-sm text-end"
        :placeholder="'0'"
        :disabled="$parent.type === 'EDIT'"
      >
      <div v-for="error of v$.item.ordered_quantity.$errors"
        class="text-danger" style="font-size: 10px;"
        :key="error.$uid"
      >
        {{ error.$message }}
      </div>
    </td>
    <!-- Unit -->
    <td class="align-top">
      <div class="input-group-sm">
        <select2
          v-model.trim="v$.item.unit_id.$model"
          @select="onSelectedUnit"
          :options="select2UnitOptions"
          :settings="{ minimumResultsForSearch: Infinity }"
          :disabled="$parent.type === 'EDIT'"
        />
      </div>
      <div v-for="error of v$.item.unit_id.$errors"
        class="text-danger" style="font-size: 10px;"
        :key="error.$uid"
      >
        {{ error.$message }}
      </div>
    </td>
    <!-- Price -->
    <td class="align-top">
      <input
        :id="'price'+index"
        type="number"
        inputmode="decimal"
        v-model.number="v$.item.price.$model"
        class="form-control shadow-none form-control-sm text-end"
        :placeholder="'0.00'"
        :disabled="$parent.type === 'EDIT'"
      >
      <div v-for="error of v$.item.price.$errors"
        class="text-danger" style="font-size: 10px;"
        :key="error.$uid"
      >
        {{ error.$message }}
      </div>
    </td>
    <!-- Discount -->
    <td class="align-top">
      <input
        :id="'discount'+index"
        type="number"
        inputmode="decimal"
        v-model.number="v$.item.discount.$model"
        class="form-control shadow-none form-control-sm text-end"
        :placeholder="'0.00'"
        :disabled="$parent.type === 'EDIT'"
      >
      <div v-for="error of v$.item.discount.$errors"
        class="text-danger" style="font-size: 10px;"
        :key="error.$uid"
      >
        {{ error.$message }}
      </div>
    </td>
    <!-- Expiry -->
    <td class="align-top">
      <input
        :id="'expiry'+index"
        type="date"
        v-model.trim="v$.item.expiry_date.$model"
        class="form-control shadow-none form-control-sm"
        :disabled="$parent.type === 'EDIT'"
      >
      <div v-for="error of v$.item.expiry_date.$errors"
        class="text-danger" style="font-size: 10px;"
        :key="error.$uid"
      >
        {{ error.$message }}
      </div>
    </td>
    <!-- Total -->
    <td class="align-top text-end">
      <span class="fw-semibold">
        {{ formatNumberTwoDigit(item.total) }}
      </span>
      <div
        v-if="taxPolicy !== 'no_tax' && item.tax_status === 'vatable'"
        style="font-size: 12px; line-height: 1.4; margin-top: 2px;"
      >
        <div class="text-muted">
          {{ $t('Before VAT') }}: {{ formatNumberTwoDigit(item.total_before_vat) }}
        </div>
        <div style="color: #0dcaf0;">
          {{ $t('VAT') }}: {{ formatNumberTwoDigit(item.total_vat) }}
        </div>
      </div>
    </td>
    <!-- Delete -->
    <td class="align-top text-center" style="width: 30px;">
      <button
        v-if="$parent.type === 'CREATE'"
        type="button"
        class="btn btn-sm text-danger border-0 p-0"
        style="width: 22px; height: 22px; line-height: 22px;"
        @click="$parent.deleteItem(index)"
      >
        <i class="fa-solid fa-xmark" style="font-size: 12px;" />
      </button>
    </td>
  </tr>
</template>

<script lang="js">
import { useVuelidate } from '@vuelidate/core';
import validation from "./assets/template/validation-item";
import moment from "moment";
import numeral from 'numeral';
import Select2 from 'vue3-select2-component';

export default {
  setup: () => ({ v$: useVuelidate() }),
  validations() {
    return validation(this.item)
  },
  props: {
    item: {
      type: Object,
      default: [{
        inventory_id: '',
        name: '',
        unit_id: '',
        inventory_unit_id: '',
        ordered_quantity: 0,
        conversion_factor: 0,
        price_before_vat: 0,
        price_vat: 0,
        price: 0,
        discount_before_vat: 0,
        discount_vat: 0,
        discount: 0,
        total_before_vat: 0,
        total_vat: 0,
        total: 0,
        expiry_date: ''
      }]
    },
    index: {
      type: Number,
      required: true
    },
    taxPolicy: {
      type: String,
      default: 'no_tax'
    },
    vatRate: {
      type: Number,
      default: 0.07
    },
    discount: {
      type: Number,
      default: 0
    },
    select2UnitOptions: {
      type: Array,
      required: true
    }
  },
  data() {
    let vm = this;
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    const datePrefix = `${year}${month}${day}`;
    return {
      lotGenarate: 'LOT-'+datePrefix+'-XXXX'
    }
  },
  components: {
    Select2
  },
  methods: {
    async onSelectedUnit(e) {
      this.item.inventory_unit_id = e.inventory_unit_id;
      this.item.conversion_factor = e.conversion_factor;
    },
    formatNumber(value) {
      return numeral(value).format("0,0");
    },
    formatNumberTwoDigit(value) {
      return numeral(value).format("0,0.00");
    },
    formatNumberDigit(value) {
      return numeral(value).format("0,0.0000");
    },
    calculateVat(totalVal) {
      if (this.taxPolicy == 'included' && this.item.tax_status == 'vatable') {
        this.item.price_before_vat = ((this.item.price || 0) / (1 + this.vatRate)).toFixed(4);
        this.item.price_vat = (this.item.price_before_vat * this.vatRate).toFixed(4);
        this.item.discount_before_vat = (this.item.discount / (1 + this.vatRate)).toFixed(4);
        this.item.discount_vat = (this.item.discount_before_vat * this.vatRate).toFixed(4);
        this.item.total = totalVal;
        this.item.total_before_vat = (totalVal / (1 + this.vatRate)).toFixed(4);
        this.item.total_vat = (this.item.total_before_vat * this.vatRate).toFixed(4);
      } else if (this.taxPolicy == 'excluded' && this.item.tax_status == 'vatable') {
        const price = Number(this.item.price) || 0;
        const discount = Number(this.item.discount) || 0;
        this.item.price_before_vat = price.toFixed(4);
        this.item.price_vat = (price * this.vatRate).toFixed(4);
        this.item.discount_before_vat = discount.toFixed(4);
        this.item.discount_vat = (discount * this.vatRate).toFixed(4);
        this.item.total_before_vat = totalVal.toFixed(4);
        this.item.total_vat = (totalVal * this.vatRate).toFixed(4);
        this.item.total = totalVal + totalVal * this.vatRate;
      } else {
        this.item.price_before_vat = (this.item.price || 0).toFixed(4);
        this.item.price_vat = (0).toFixed(4);
        this.item.discount_before_vat = (this.item.discount || 0).toFixed(4);
        this.item.discount_vat = (0).toFixed(4);
        this.item.total_before_vat = (totalVal).toFixed(4);
        this.item.total_vat = (0).toFixed(4);
        this.item.total = totalVal;
      }
      if (this.discount > 0) {
        this.$emit('calculate-discount');
      }
    },
  },
  async mounted() {},
  computed: {
    total: function() {
      const totalPrice = this.item.ordered_quantity * this.item.price;
      return totalPrice - this.item.discount;
    },
  },
  watch: {
    total: {
      handler(totalVal) {
        this.calculateVat(totalVal);
      },
      immediate: true
    },
    taxPolicy() {
      this.calculateVat(this.total);
    },
    vatRate() {
      this.calculateVat(this.total);
    }
  }
}
</script>

<style scoped>
.item-row td {
  padding: 8px 6px;
  border-bottom: 1px solid #f0f0f0;
}
.item-row:last-child td {
  border-bottom: none;
}
.item-row:hover {
  background-color: #f8f9fc !important;
}
</style>
